import { useEffect, useRef, useState } from 'react';
import type { QuoteOptionSearchResult } from '@techgitter/contracts';
import { searchQuoteOptions } from '../services/quotesApi';

export interface UseQuoteOptionSearchResult {
  results: QuoteOptionSearchResult[];
  isLoading: boolean;
  error: string | null;
}

const DEBOUNCE_MS = 250;

/**
 * Debounced search hook for the quote-options price book.
 *
 * Fires `searchQuoteOptions` 250ms after `query` settles. Aborts the in-flight
 * request when `query` changes again or the component unmounts.
 */
export function useQuoteOptionSearch(query: string): UseQuoteOptionSearchResult {
  const [results, setResults] = useState<QuoteOptionSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const handle = window.setTimeout(async () => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const matches = await searchQuoteOptions(trimmed, 50, controller.signal);
        if (!controller.signal.aborted) {
          setResults(matches);
        }
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
      controllerRef.current?.abort();
    };
  }, [query]);

  return { results, isLoading, error };
}
